<template>
  <div v-if="isOpen" class="modal-overlay" @click.self="closeModal">
    <div class="modal-container premium-scroll">
      <!-- Close Button -->
      <button class="close-button" @click="closeModal">
        <i class="material-icons">close</i>
      </button>

      <!-- Header -->
      <div class="modal-header">
        <h2>{{ $t('shop.paralleliums.title') }}</h2>
        <div class="header-decoration"></div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-container">
        <div class="spinner"></div>
        <p>{{ $t('shop.loading') }}</p>
      </div>

      <!-- Packages Grid -->
      <div v-else class="packages-grid">
        <div
          v-for="pkg in packagesWithPromotions"
          :key="pkg.id"
          class="package-card"
          :class="{
            'selected': selectedPackage === pkg.id,
            'featured': pkg.featured,
            'most-sold': pkg.mostSold
          }"
          @click="selectPackage(pkg)"
        >
          <!-- Most Sold Badge -->
          <div v-if="pkg.mostSold" class="most-sold-badge">
            <i class="material-icons">star</i>
            <span>{{ $t('shop.mostSold') }}</span>
          </div>

          <!-- Promotion Badge -->
          <div v-if="pkg.hasPromotion" class="promo-badge pulse">
            <i class="material-icons">local_fire_department</i>
            <span>+{{ pkg.bonusPercentage }}%</span>
          </div>

          <!-- Package Content -->
          <div class="package-icon">
            <i class="material-icons">auto_awesome</i>
          </div>

          <div class="package-info">
            <h3 class="package-name">{{ pkg.name }}</h3>

            <!-- Amount Display -->
            <div class="amount-display">
              <div class="base-amount">
                <i class="material-icons">casino</i>
                <span class="amount-value">{{ formatNumber(pkg.amount) }}</span>
              </div>

              <!-- Bonus Amount -->
              <div v-if="pkg.hasPromotion" class="bonus-amount">
                <i class="material-icons">add</i>
                <span class="bonus-value">{{ formatNumber(pkg.bonusAmount) }}</span>
                <span class="bonus-label">{{ $t('shop.bonus') }}</span>
              </div>
            </div>

            <!-- Total Amount (with promotion) -->
            <div v-if="pkg.hasPromotion" class="total-amount">
              <span>{{ $t('shop.totalAmount') }}:</span>
              <span class="total-value">{{ formatNumber(pkg.totalAmount) }}</span>
              <i class="material-icons">casino</i>
            </div>
          </div>

          <!-- Price Section -->
          <div class="package-price">
            <div class="price-main">
              <span class="currency">{{ currencySymbol(pkg) }}</span>
              <span class="price-value">{{ formatPrice(pkg.price) }}</span>
            </div>

            <!-- Price Comparison -->
            <div class="price-comparison">
              <span>{{ $t('shop.pricePerParallelium') }}:</span>
              <span class="price-per">{{ currencySymbol(pkg) }} {{ pkg.pricePerParallelium }}</span>
            </div>
          </div>

          <!-- Buy Button -->
          <button
            class="btn-buy"
            :class="{ 'btn-selected': selectedPackage === pkg.id }"
            :disabled="checkoutLoading"
            @click.stop="buyPackage(pkg)"
          >
            <i class="material-icons">shopping_cart</i>
            <span>{{ $t('shop.buy') }}</span>
          </button>
        </div>
      </div>

      <!-- Footer Info -->
      <div class="modal-footer">
        <i class="material-icons">info</i>
        <p>{{ $t('shop.securePayment') }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'

export default {
  name: 'BuyParalleliums',
  props: {
    isOpen: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      selectedPackage: null,
      loading: false,
      // Evita abrir duas sessões de checkout com dois cliques no botão.
      checkoutLoading: false
    }
  },
  computed: {
    ...mapState('monetization', ['packages', 'promotions']),
    ...mapGetters('monetization', ['packagesWithPromotions'])
  },
  async mounted() {
    await this.loadPackages()
  },
  methods: {
    ...mapActions('monetization', ['fetchPackages', 'fetchPromotions', 'createCheckout']),
    async loadPackages() {
      this.loading = true
      try {
        await this.fetchPackages()
        await this.fetchPromotions()
      } catch (error) {
        console.error('Error loading packages:', error)
      } finally {
        this.loading = false
      }
    },
    selectPackage(pkg) {
      this.selectedPackage = pkg.id
    },
    async buyPackage(pkg) {
      if (this.checkoutLoading) return
      try {
        this.checkoutLoading = true
        this.selectedPackage = pkg.id
        
        // Check if user is authenticated (o módulo auth guarda em `token`)
        if (!this.$store.state.auth.token) {
          alert(this.$t('shop.loginRequired'))
          return
        }

        // Create checkout session and redirect to Stripe
        await this.createCheckout(pkg.id)
      } catch (error) {
        console.error('Error creating checkout:', error)
        // Mensagem real do servidor (formato Strapi 5) quando houver.
        alert(error.response?.data?.error?.message || this.$t('shop.errorCheckout'))
      } finally {
        this.checkoutLoading = false
      }
    },
    formatNumber(num) {
      return Number(num || 0).toLocaleString('pt-BR')
    },
    // `price` vem do pacote em reais (decimal), a mesma unidade que o admin
    // digita. A conversão para centavos, que é o que a Stripe cobra, acontece só
    // no servidor.
    formatPrice(price) {
      return Number(price || 0).toFixed(2).replace('.', ',')
    },
    // Pacote em dólar mostrava "R$" mesmo assim.
    currencySymbol(pkg) {
      return pkg.currency === 'usd' ? 'US$' : 'R$'
    },
    closeModal() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped lang="scss">
// Cyberpunk Colors
$primary-red: #ff4646;
$primary-blue: #3498db;
$dark-bg: #1a1a1a;
$darker: #0d0d0d;
$gold: #ffd700;

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  padding: 20px;
  animation: fadeIn 0.3s ease;
}

.modal-container {
  background: linear-gradient(135deg, $darker 0%, $dark-bg 100%);
  border: 2px solid $primary-red;
  border-radius: 12px;
  box-shadow: 0 0 40px rgba($primary-red, 0.5);
  max-width: 1200px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  padding: 30px;
  animation: slideUp 0.3s ease;
}

.close-button {
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba($primary-red, 0.2);
  border: 2px solid $primary-red;
  color: $primary-red;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 10;

  &:hover {
    background: $primary-red;
    color: white;
    transform: rotate(90deg);
  }

  .material-icons {
    font-size: 24px;
  }
}

.modal-header {
  text-align: center;
  margin-bottom: 30px;

  h2 {
    font-family: 'Cinzel Decorative', serif;
    font-size: 2.5rem;
    color: $primary-red;
    text-shadow: 0 0 20px rgba($primary-red, 0.5);
    margin-bottom: 10px;
  }

  .header-decoration {
    width: 100px;
    height: 3px;
    background: linear-gradient(90deg, transparent, $primary-red, transparent);
    margin: 0 auto;
  }
}

.loading-container {
  text-align: center;
  padding: 60px 20px;

  .spinner {
    width: 60px;
    height: 60px;
    border: 4px solid rgba($primary-red, 0.2);
    border-top-color: $primary-red;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 20px;
  }

  p {
    color: $primary-blue;
    font-size: 1.1rem;
  }
}

.packages-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 25px;
  margin-bottom: 30px;
}

.package-card {
  background: rgba($dark-bg, 0.7);
  border: 2px solid rgba($primary-blue, 0.3);
  border-radius: 10px;
  padding: 25px;
  position: relative;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover, &.selected {
    border-color: $primary-red;
    box-shadow: 0 0 25px rgba($primary-red, 0.4);
    transform: translateY(-5px);
  }

  &.featured {
    border-color: $gold;

    &:hover, &.selected {
      box-shadow: 0 0 30px rgba($gold, 0.5);
    }
  }

  &.most-sold {
    border-width: 3px;
  }
}

.most-sold-badge {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, $gold, #ffaa00);
  color: $darker;
  padding: 6px 16px;
  border-radius: 20px;
  font-weight: bold;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 5px;
  box-shadow: 0 3px 10px rgba($gold, 0.5);
  z-index: 1;

  .material-icons {
    font-size: 16px;
  }
}

.promo-badge {
  position: absolute;
  top: 15px;
  right: 15px;
  background: linear-gradient(135deg, $primary-red, #ff1a1a);
  color: white;
  padding: 8px 14px;
  border-radius: 8px;
  font-weight: bold;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 5px;
  box-shadow: 0 0 20px rgba($primary-red, 0.6);
  z-index: 1;

  .material-icons {
    font-size: 18px;
  }

  &.pulse {
    animation: pulse 2s infinite;
  }
}

.package-icon {
  text-align: center;
  margin-bottom: 15px;

  .material-icons {
    font-size: 60px;
    color: $gold;
    filter: drop-shadow(0 0 10px rgba($gold, 0.6));
  }
}

.package-info {
  margin-bottom: 20px;

  .package-name {
    font-family: 'Orbitron', sans-serif;
    font-size: 1.4rem;
    color: $primary-blue;
    text-align: center;
    margin-bottom: 15px;
  }

  .amount-display {
    background: rgba($darker, 0.6);
    border-radius: 8px;
    padding: 12px;
    margin-bottom: 10px;

    .base-amount {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      font-size: 1.6rem;
      color: white;
      font-weight: bold;

      .material-icons {
        color: $gold;
        font-size: 28px;
      }
    }

    .bonus-amount {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      margin-top: 8px;
      color: $primary-red;
      font-size: 1.2rem;
      font-weight: bold;

      .bonus-label {
        font-size: 0.9rem;
        opacity: 0.8;
      }
    }
  }

  .total-amount {
    background: rgba($primary-red, 0.1);
    border: 1px solid $primary-red;
    border-radius: 6px;
    padding: 10px;
    text-align: center;
    color: $primary-red;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    .total-value {
      font-size: 1.4rem;
    }

    .material-icons {
      color: $gold;
      font-size: 20px;
    }
  }
}

.package-price {
  background: rgba($darker, 0.6);
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;

  .price-main {
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 5px;
    margin-bottom: 8px;

    .currency {
      font-size: 1.2rem;
      color: $gold;
    }

    .price-value {
      font-size: 2rem;
      font-weight: bold;
      color: $gold;
      text-shadow: 0 0 10px rgba($gold, 0.5);
    }
  }

  .price-comparison {
    text-align: center;
    font-size: 0.9rem;
    color: rgba(white, 0.7);

    .price-per {
      color: $primary-blue;
      font-weight: bold;
    }
  }
}

.btn-buy {
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, $primary-blue, #2980b9);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #2980b9, $primary-blue);
    box-shadow: 0 0 20px rgba($primary-blue, 0.5);
    transform: translateY(-2px);
  }

  &.btn-selected {
    background: linear-gradient(135deg, $primary-red, #c0392b);

    &:hover {
      background: linear-gradient(135deg, #c0392b, $primary-red);
      box-shadow: 0 0 20px rgba($primary-red, 0.5);
    }
  }

  .material-icons {
    font-size: 22px;
  }
}

.modal-footer {
  text-align: center;
  padding-top: 20px;
  border-top: 1px solid rgba($primary-blue, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: rgba(white, 0.6);

  .material-icons {
    color: $primary-blue;
    font-size: 20px;
  }
}

// Animations
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes pulse {
  0%, 100% {
    box-shadow: 0 0 20px rgba($primary-red, 0.6);
  }
  50% {
    box-shadow: 0 0 30px rgba($primary-red, 1);
  }
}

// Responsive
@media (max-width: 768px) {
  .modal-container {
    padding: 20px;
  }

  .modal-header h2 {
    font-size: 1.8rem;
  }

  .packages-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .package-info .package-name {
    font-size: 1.2rem;
  }

  .package-price .price-main .price-value {
    font-size: 1.6rem;
  }
}
</style>
